import { ReactNode, useState } from 'react'
import Styles from './PostStatusDropdown.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Drawer } from 'components/Drawer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { SocialChannelType, platformNameFromChannel } from 'common/ShareSheetV2/Screens/Default/Default'
import { ArrowUpRightSquareContained } from 'blaze-ui/icons'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import cx from 'classnames'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { DrawerProps } from 'components/Drawer/Root'
import { FacebookInitialization, InstagramInitialization } from 'components/IntegrationInitialization'
import XInitialization from 'components/IntegrationInitialization/XInitialization/XInitialization'
import { WordPressInitializationContent } from 'components/IntegrationInitialization/WordPressInitialization/WordPressInitialization'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface ConnectAccountDrawerProps extends DrawerProps {
  trigger?: ReactNode
  channel: SocialChannelType
  isNested?: boolean
}

const connectScreenshotForPlatform = (channel: SocialChannelType) => {
  switch (channel) {
    case ChannelType.FacebookPost:
      return 'https://res.cloudinary.com/almanac/image/upload/v1720709409/Integration%20Connect%20Request/FacebookAccessRequest_emjasi.png'
    case ChannelType.InstagramPosts:
    case ChannelType.InstagramReel:
      return 'https://res.cloudinary.com/almanac/image/upload/v1720709409/Integration%20Connect%20Request/InstagramAccessRequest_ogdcng.png'
    case ChannelType.LinkedInPosts:
      return 'https://res.cloudinary.com/almanac/image/upload/v1720536102/Integration%20Connect%20Request/LinkedInAccessRequest_aubead.png'
    case ChannelType.Tweets:
    case ChannelType.TweetThread:
      return 'https://res.cloudinary.com/almanac/image/upload/v1720709409/Integration%20Connect%20Request/XAccessRequest_nn0ggw.png'
  }
}

export const ConnectAccountDrawer = observer(({ trigger, channel, isNested, ...props }: ConnectAccountDrawerProps) => {
  const connectScreenshot = connectScreenshotForPlatform(channel)
  const { drawersStore } = useRootStore()
  const [wordpressStep, setWordpressStep] = useState(1)
  const { initiateOauth } = useInitiateOauth()
  const connect = () => {
    initiateOauth(providerFromChannelType[channel] as ExternalIntegrationEnum)
  }

  return (
    <ConditionalWrapper
      condition={isNested}
      wrapper={(children) => <Drawer.NestedRoot {...props}>{children}</Drawer.NestedRoot>}
      elseWrapper={(children) => <Drawer.Root {...props}>{children}</Drawer.Root>}
    >
      <Drawer.Portal>
        <Drawer.Overlay />
        <Drawer.Content>
          <Drawer.InnerContent color={channel === ChannelType.LinkedInPosts ? 'var(--light-100)' : undefined}>
            <Drawer.Top>
              <div />
              <Drawer.Title asChild>
                <Heading level={5}>Connect Account</Heading>
              </Drawer.Title>
            </Drawer.Top>
            <Drawer.Container>
              {channel === ChannelType.InstagramPosts && <InstagramInitialization useDrawer />}
              {channel === ChannelType.FacebookPost && <FacebookInitialization useDrawer />}
              {channel === ChannelType.Tweets && <XInitialization useDrawer />}
              {channel === ChannelType.BlogPost && (
                <WordPressInitializationContent
                  step={wordpressStep}
                  setStep={setWordpressStep}
                  onSubmit={() => drawersStore.closeDrawer()}
                />
              )}
              {channel === ChannelType.LinkedInPosts && (
                <div className={Styles.connectAccountDrawer}>
                  {connectScreenshot && (
                    <img
                      className={cx({ [Styles.withBorder]: channel !== ChannelType.LinkedInPosts })}
                      alt="screenshot"
                      src={connectScreenshot}
                    />
                  )}
                  <div className={Styles.content}>
                    <Heading level={4}>You have to connect your account first</Heading>
                    <Text>
                      We are sending you to {platformNameFromChannel[channel]} real quick to sign in and connect your
                      account to Blaze.
                    </Text>
                  </div>
                  <Button fullWidth size="lg" endIcon={ArrowUpRightSquareContained} onPress={connect}>
                    Go to {platformNameFromChannel[channel]}
                  </Button>
                </div>
              )}
            </Drawer.Container>
          </Drawer.InnerContent>
        </Drawer.Content>
      </Drawer.Portal>
    </ConditionalWrapper>
  )
})
